import React, { useEffect, useState } from "react";
import { socketInstance } from "../socket/socket";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import { updateMessageOptions } from "../store/Slices/MessageOptionsSlice";
import { updateViewState } from "../store/Slices/ViewManagerSlice";
import { useNavigate } from "react-router-dom";
import { updateCurrentConversation } from "../store/Slices/CurrentConversationSlice";
import { updatePeerData } from "../store/Slices/PeerJsSlice";
import axiosInstance from "../store/api/axiosInstance";
import { chatListFullPayload } from "../utils/socketQueries";
import { setCallPeerSnapshot } from "../utils/callPeerSnapshot";

const NOTIFICATION_PROMPT_KEY = "notification_prompt_answered";

export default function LoadInitialData() {
  const dispatch = useAppDispatch();
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const userData = useAppSelector((state) => state.userData);
  const navigate = useNavigate();
  // is_update is for weather chat list is updated or not
  const [is_updated, setis_updated] = useState(true);
  const chatList = useAppSelector((state) => state.chatList);

  // Обработка клика "Принять" в push-уведомлении о звонке
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get("action");
    const callId = params.get("call_id");
    const chatId = params.get("chat_id");
    const roomId = params.get("room_id");
    const callType = params.get("call_type") || "video";
    if (action === "accept" && callId && chatId && roomId) {
      void (async () => {
        try {
          const { data } = await axiosInstance.get<{ engine?: string; livekit_url?: string }>(
            "config/call-engine",
          );
          const engine = data?.engine === "peerjs" ? "peerjs" : "livekit";
          sessionStorage.setItem("media_engine", engine);
          if (data?.livekit_url) {
            sessionStorage.setItem("livekit_url", data.livekit_url);
          }
        } catch {
          sessionStorage.setItem("media_engine", "livekit");
        }
        sessionStorage.setItem("call_id", callId);
        sessionStorage.setItem("chat_id", chatId);
        sessionStorage.setItem("room_id", roomId);
        sessionStorage.setItem("call_type", callType);
        sessionStorage.setItem("is_caller", "false");
        sessionStorage.setItem("call_joined", "true");
        sessionStorage.removeItem("livekit_token");
        setCallPeerSnapshot({
          title: params.get("call_title") || params.get("caller_name") || "",
          avatar: params.get("call_avatar") || "",
          chatId,
          chatType: params.get("chat_type") || "",
        });
        dispatch(
          updatePeerData({
            call_id: Number(callId),
            chat_id: chatId,
            room_id: roomId,
            call_joined: true,
          }),
        );
        dispatch(updateViewState({ show_accept_call_modal: false }));
        window.history.replaceState({}, "", window.location.pathname || "/chat");
        navigate("/video-call");
      })();
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    if (chatList?.Chats?.length == 0) {
      dispatch(
        updateViewState({
          is_chatlist_loading: true,
        }),
      );
    }
    socketInstance().emit("chat_list", chatListFullPayload());
  }, []);

  // Show enable notifications modal on first visit (or new browser) when permission not yet asked
  useEffect(() => {
    if (
      userData.user_id >= 1 &&
      "Notification" in window &&
      Notification.permission === "default" &&
      localStorage.getItem(NOTIFICATION_PROMPT_KEY) !== "declined"
    ) {
      dispatch(updateViewState({ show_enable_notifications_modal: true }));
    }
  }, [userData.user_id, dispatch]);

  useEffect(() => {
    if (userData.user_id >= 1 && userData.full_name == "") {
      navigate("/user-details");
    } else if (
      userData.user_id >= 1 &&
      (userData.profile_pic == "" ||
        userData.profile_pic.includes("/not-found-images/"))&& window.location.pathname == "/user-details"
    ) {
      navigate("/select-profile");
    }
  }, [userData]);

  useEffect(() => {
    if (
      currentConversationData?.chat_id <= 0 &&
      currentConversationData?.user_id <= 0
    ) {
      return;
    }
    // alert(currentConversationData.chat_id)
    dispatch(
      updateMessageOptions({
        isMessageLoading: true,
      }),
    );
  }, [currentConversationData?.chat_id, currentConversationData?.user_id]);

  useEffect(() => {
    let conversationData = null;
    try {
      const raw = sessionStorage.getItem("currentConversation");
      conversationData = raw ? JSON.parse(raw) : null;
    } catch {
      conversationData = null;
    }

    (chatList?.Chats ?? []).forEach((chat) => {
      const record = chat?.Records?.[0];
      if (!record) return;
      if (record.chat_id == conversationData?.chat_id && is_updated) {
        setis_updated(false);
        dispatch(
          updateCurrentConversation({
            chat_id: record.chat_id,
            chat_type: record.chat_type,
            full_name: chat?.PeerUserData?.full_name,
            group_name: record.group_name,
            group_icon: record.group_icon,
            profile_pic: chat?.PeerUserData?.profile_pic,
            blur_hash:
              record.chat_type == "group"
                ? record.blur_hash || record.blurHash || ""
                : chat?.PeerUserData?.blur_hash ||
                  chat?.PeerUserData?.blurHash ||
                  "",
            user_id: chat?.PeerUserData?.user_id,
            is_group_blocked: record?.blocked_by?.includes(
              userData.user_id.toString(),
            ),
            other_user_blocked_you:
              (record?.blocked_by?.length ?? 0) >= 1 &&
              !record?.blocked_by?.includes(userData.user_id.toString()),
            user_name: chat?.PeerUserData?.user_name,
            email_id: chat?.PeerUserData?.email,
            unseen_count: record.unseen_count,
            updatedAt: chat?.PeerUserData?.updatedAt ?? "",
            is_message_list_empty: (record?.Messages?.length ?? 0) === 0,
            createdAt: record.createdAt,
            bio: chat?.PeerUserData?.bio,
            group_description: record.group_description,
            mobile_num: chat?.PeerUserData?.mobile_num ?? "",
          }),
        );
      }
    });

    const activeCallChatId = Number(sessionStorage.getItem("chat_id") || 0);
    const hasCallTitle = !!sessionStorage.getItem("call_title")?.trim();
    if (sessionStorage.getItem("call_id") && activeCallChatId > 0 && !hasCallTitle) {
      for (const chat of chatList?.Chats ?? []) {
        const record = chat?.Records?.[0];
        if (!record || Number(record.chat_id) !== activeCallChatId) continue;
        const isGroup =
          record.chat_type === "group" || record.chat_type === "public_group";
        setCallPeerSnapshot({
          title: isGroup
            ? record.group_name || "Group call"
            : chat?.PeerUserData?.full_name ||
              chat?.PeerUserData?.user_name ||
              "",
          avatar: isGroup
            ? record.group_icon || ""
            : chat?.PeerUserData?.profile_pic || "",
          chatId: activeCallChatId,
          chatType: record.chat_type,
        });
        break;
      }
    }

    // dispatch(updateCurrentConversation(conversationData));
    if (conversationData && Object.keys(conversationData)?.length > 0) {
      dispatch(
        updateViewState({
          show_chats_sidebar: false,
        }),
      );
    }
  }, [dispatch, chatList]);

  return <></>;
}
